import { useId, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AdminDeletionRow } from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useToast } from '@/components/useToast';
import { useCanWrite } from '@/auth/authHooks';
import { formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { CancelDeletionDialog } from './CancelDeletionDialog';
import { RequestDeletionDialog } from './RequestDeletionDialog';
import { StatusChip } from '@/components/status/StatusChip';
import { useDeletionRequests } from './useDeletionRequests';

export function DataRequestsSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const canWrite = useCanWrite();
  const { status, data, error, reload, request, cancel } = useDeletionRequests();
  const [requestOpen, setRequestOpen] = useState(false);
  const [cancelRow, setCancelRow] = useState<AdminDeletionRow | null>(null);
  const headingId = useId();
  const rows = data?.requests ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between', mb: 0.5 }}
      >
        <Box>
          <Typography variant="h3" component="h2" id={headingId}>
            {t('settings.data.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('settings.data.lead')}
          </Typography>
        </Box>
        {canWrite ? (
          <Button variant="outlined" color="error" onClick={() => setRequestOpen(true)}>
            {t('settings.data.requestAction')}
          </Button>
        ) : null}
      </Stack>
      {status === 'loading' && !data ? (
        <Box role="status" aria-label={t('common.loading')} sx={{ mt: 2 }}>
          <Skeleton variant="rounded" height={160} />
        </Box>
      ) : null}
      {status === 'error' ? (
        <Box sx={{ mt: 2 }}>
          <ApiErrorState error={error} onRetry={reload} />
        </Box>
      ) : null}
      {data ? (
        <Paper variant="outlined" sx={{ mt: 2, overflow: 'auto' }}>
          <Table size="small" aria-labelledby={headingId}>
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.data.col.subscriber')}</TableCell>
                <TableCell>{t('settings.data.col.requested')}</TableCell>
                <TableCell>{t('settings.data.col.by')}</TableCell>
                <TableCell>{t('settings.data.col.status')}</TableCell>
                <TableCell>{t('settings.data.col.erasesOn')}</TableCell>
                <TableCell align="right">{t('settings.data.col.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>{t('settings.data.empty')}</TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const statusNode = <StatusChip domain="deletion" status={row.status} />;
                  return (
                    <TableRow key={row.id ?? `${row.userId}-${row.requestedAt}`}>
                      <TableCell component="th" scope="row">
                        {row.displayName ?? t('settings.unavailable')}
                      </TableCell>
                      <TableCell>{formatInstantOrDash(row.requestedAt, locale, t('settings.unavailable'))}</TableCell>
                      <TableCell>{row.requestedBy ?? t('settings.unavailable')}</TableCell>
                      <TableCell>{statusNode}</TableCell>
                      <TableCell>
                        {row.status === 'PENDING'
                          ? formatInstantOrDash(row.coolingOffUntil, locale, t('settings.unavailable'))
                          : t('settings.unavailable')}
                      </TableCell>
                      <TableCell align="right">
                        {canWrite && row.status === 'PENDING' ? (
                          <Button size="small" onClick={() => setCancelRow(row)}>
                            {t('settings.data.cancelAction')}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
      <RequestDeletionDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSubmit={async (body) => {
          await request(body);
          showToast(t('settings.data.toast.requested'));
        }}
      />
      <CancelDeletionDialog
        open={cancelRow !== null}
        row={cancelRow}
        onClose={() => setCancelRow(null)}
        onSubmit={async (id, body) => {
          await cancel(id, body);
          showToast(t('settings.data.toast.cancelled'));
        }}
      />
    </Paper>
  );
}
